/**
 * Who is signed in, the company they work in, and «Logg ut» (SB-23, SB-321). Shown only while
 * sign-in is enforced: with `auth_enabled` off the app bar stays as it was.
 *
 * A person who may work in two or more companies switches here. The switch loads the page again,
 * so nothing the last company loaded stays on screen.
 */
import {useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {Box, Chip, Divider, IconButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Tooltip, Typography} from '@mui/material';
import {
    AccountCircle as AccountIcon,
    Check as CheckIcon,
    Groups as MembersIcon,
    Logout as LogoutIcon,
} from '@mui/icons-material';

import {useAuth} from '../auth/AuthContext';
import {companyRoleLabel} from '../auth/companyLabels';

export default function UserMenu() {
    const auth = useAuth();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [switching, setSwitching] = useState<string | null>(null);
    const [refused, setRefused] = useState<string | null>(null);
    if (!auth.authEnabled || !auth.user) return null;

    const {name, email, role} = auth.user;
    const open = Boolean(anchor);
    const {company, companies} = auth;
    const close = () => {
        setAnchor(null);
        setRefused(null);
    };

    const choose = async (companyId: string) => {
        if (companyId === company?.id || switching) return;
        setSwitching(companyId);
        const outcome = await auth.switchCompany(companyId);  // resolves only when it did not switch
        setSwitching(null);
        setRefused(outcome === 'refused'
            ? 'Du har ikke lenger tilgang til det firmaet.'
            : 'Kunne ikke bytte firma. Prøv igjen.');
    };

    return (
        <>
            <Tooltip title={name || email || 'Konto'}>
                <IconButton
                    color="inherit"
                    size="small"
                    onClick={event => setAnchor(event.currentTarget)}
                    aria-label="Konto"
                    aria-haspopup="menu"
                    aria-controls={open ? 'user-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    sx={{ml: 1}}
                >
                    <AccountIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
            <Menu
                id="user-menu"
                anchorEl={anchor}
                open={open}
                onClose={close}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Box sx={{px: 2, pt: 1, pb: 1.5, maxWidth: 320}}>
                    <Typography variant="subtitle1" noWrap>{name || email}</Typography>
                    {name && email && (
                        <Typography variant="body2" color="text.secondary" noWrap>{email}</Typography>
                    )}
                    {role === 'admin' && <Chip size="small" label="SuccessBuilder-administrator" sx={{mt: 1}}/>}
                </Box>
                <Divider/>
                {companies.length >= 2 && (
                    <ListSubheader sx={{lineHeight: '32px'}}>Bytt firma</ListSubheader>
                )}
                {companies.length >= 2 && companies.map(c => {
                    const active = c.id === company?.id;
                    return (
                        <MenuItem
                            key={c.id}
                            selected={active}
                            disabled={switching !== null}
                            onClick={() => void choose(c.id)}
                            sx={{maxWidth: 320}}
                        >
                            <ListItemIcon>{active && <CheckIcon fontSize="small"/>}</ListItemIcon>
                            <ListItemText
                                primary={c.name || 'Uten navn'}
                                secondary={switching === c.id ? 'Bytter …' : companyRoleLabel(c.role)}
                                slotProps={{primary: {noWrap: true}}}
                            />
                        </MenuItem>
                    );
                })}
                {refused && (
                    <Typography variant="body2" color="error" role="alert" sx={{px: 2, py: 1, maxWidth: 320}}>
                        {refused}
                    </Typography>
                )}
                {company && (
                    <MenuItem component={RouterLink} to="/company" onClick={close}>
                        <ListItemIcon><MembersIcon fontSize="small"/></ListItemIcon>
                        <ListItemText
                            primary="Firma og medlemmer"
                            secondary={companies.length < 2 ? company.name : undefined}
                            slotProps={{secondary: {noWrap: true}}}
                        />
                    </MenuItem>
                )}
                {(companies.length >= 2 || company) && <Divider/>}
                <MenuItem
                    onClick={() => {
                        close();
                        void auth.signOut();
                    }}
                >
                    <ListItemIcon><LogoutIcon fontSize="small"/></ListItemIcon>
                    Logg ut
                </MenuItem>
            </Menu>
        </>
    );
}
